import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  makeStyles,
} from '@material-ui/core';
import moment from 'logic/moment';
import CircularProgress from 'components/shared/CircularProgress';
import ComplaintDeadline from './ComplaintDeadline';
import ComplaintStatusBadge from './ComplaintStatusBadge';
import { formatCottageLabel } from '../constants';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginBottom: theme.spacing(2),
  },
}));

const ComplaintsList = ({ list, isLoading, basePath, view, emptyMessage }) => {
  const classes = useStyles();
  const intl = useIntl();

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!list || list.length === 0) {
    return (
      <Paper className={classes.paper}>
        <ListItem>
          <ListItemText
            secondary={
              emptyMessage || (
                <FormattedMessage id="complaints.noComplaints" defaultMessage="No complaints yet" />
              )
            }
          />
        </ListItem>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <List>
        {list.map((complaint) => {
          const category = complaint.category?.key
            ? intl.formatMessage({
                id: `complaints.category.${complaint.category.key}`,
                defaultMessage: complaint.category.name,
              })
            : complaint.category?.name || '';
          const cottageLabel =
            view === 'host' ? formatCottageLabel(complaint.booking?.Cottage) : null;
          const subtitle = cottageLabel
            ? `#${complaint.booking_id} - ${cottageLabel}`
            : `#${complaint.booking_id}`;

          return (
            <ListItem
              key={complaint.id}
              button
              component={Link}
              to={`${basePath}/${complaint.id}`}
              divider
            >
              <ListItemText
                primary={category}
                secondary={
                  <>
                    {subtitle}
                    <br />
                    {moment(complaint.created_at).format('LL')}
                    {complaint.deadline_at && (
                      <>
                        {' \u2013 '}
                        <ComplaintDeadline complaint={complaint} view={view} />
                      </>
                    )}
                  </>
                }
              />
              <ListItemSecondaryAction>
                <ComplaintStatusBadge status={complaint.status} view={view} />
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};

ComplaintsList.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      booking_id: PropTypes.number,
      status: PropTypes.string,
      category: PropTypes.shape({ name: PropTypes.string }),
      created_at: PropTypes.string,
      deadline_at: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  basePath: PropTypes.string.isRequired,
  view: PropTypes.oneOf(['guest', 'host']),
  emptyMessage: PropTypes.node,
};

export default ComplaintsList;
